import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelection } from '../store/selection.ts';
import type { useContextMenu } from './useContextMenu.ts';
import type { useFolderActions } from './useFolderActions.ts';
import { previewKindOf } from '../lib/format.ts';
import type { ApiNode } from '../api/types.ts';

interface Input {
  items: ApiNode[];
  /** 라우트 접두. 개인 드라이브는 '/files', 공유 폴더는 '/ns/<id>' */
  base: string;
  actions: ReturnType<typeof useFolderActions>;
  menu: ReturnType<typeof useContextMenu>;
  onOpenPreview: (node: ApiNode) => void;
  onContextTarget: (node: ApiNode) => void;
  onDetailsFollow: (node: ApiNode) => void;
  onRenameDone: () => void;
}

/** FileTable 이 행마다 호출하는 콜백 묶음. */
export interface RowHandlers {
  onOpen: (node: ApiNode) => void;
  onSelect: (node: ApiNode, event: React.MouseEvent) => void;
  onContextMenu: (node: ApiNode, event: React.MouseEvent) => void;
  onRename: (node: ApiNode, name: string) => void;
  onDropInto: (folder: ApiNode, paths: string[]) => void;
  onDragStartPaths: (node: ApiNode) => string[];
}

/**
 * 파일 목록 행의 조작을 한곳에 모은다.
 *
 * 열기·선택·오른쪽 클릭·이름 변경·끌어놓기가 모두 선택 상태와 얽혀 있어서
 * 페이지 컴포넌트에 두면 렌더링 코드 사이에 로직이 흩어진다.
 */
export function useFileRowHandlers(input: Input): RowHandlers {
  const navigate = useNavigate();
  const selected = useSelection((s) => s.paths);
  const selectOnly = useSelection((s) => s.selectOnly);
  const toggle = useSelection((s) => s.toggle);
  const range = useSelection((s) => s.range);
  const clearSelection = useSelection((s) => s.clear);

  const { items, base, actions, menu } = input;
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const open = useCallback((node: ApiNode) => {
    if (node.type === 'folder') {
      navigate(`${base}${node.path}`);
      clearSelection();
      return;
    }
    // 미리보기가 되는 형식이면 띄우고, 아니면 바로 내려받는다
    if (previewKindOf(node.name, node.mime) !== 'none') {
      input.onOpenPreview(node);
      return;
    }
    void actions.download([node.path], node.name);
  }, [navigate, clearSelection, actions, base, input]);

  return useMemo(() => ({
    onOpen: open,

    onSelect: (node, event) => {
      if (event.shiftKey) range(items.map((i) => i.path), node.path);
      else if (event.metaKey || event.ctrlKey) toggle(node.path);
      else selectOnly(node.path);
      input.onDetailsFollow(node);
    },

    onContextMenu: (node, event) => {
      // 고르지 않은 행에서 오른쪽 클릭하면 그 행만 고른 것으로 본다.
      // 안 그러면 메뉴가 화면에 보이지 않는 선택에 동작한다.
      if (!selectedSet.has(node.path)) selectOnly(node.path);
      input.onContextTarget(node);
      menu.openAt(event);
    },

    onRename: (node, name) => {
      input.onRenameDone();
      actions.rename.mutate({ from: node.path, name });
    },

    onDropInto: (folder, paths) => {
      // 폴더를 자기 안으로 넣으면 트리가 끊긴다
      const moving = paths.filter((p) => p !== folder.path);
      if (moving.length > 0) {
        actions.move.mutate({ paths: moving, targetFolder: folder.path });
      }
    },

    // 고른 것 위에서 끌면 전체를, 아니면 그 행만 끈다
    onDragStartPaths: (node) => (selectedSet.has(node.path) ? selected : [node.path]),
  }), [open, items, range, toggle, selectOnly, menu, actions, selected, selectedSet, input]);
}
